import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const emptyExercise = () => ({ name: '', sets: 3, reps: 10, rest_time: '60s', notes: '' });
const emptyPlanForm = () => ({
  title: '',
  branch_id: '',
  trainer_id: '',
  member_id: '',
  exercises: {},
});

export default function OwnerWorkoutPlanBuilder() {
  useAuth();
  const [plans, setPlans] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [members, setMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modalTrainers, setModalTrainers] = useState([]);
  const [modalMembers, setModalMembers] = useState([]);
  const [loadingModalData, setLoadingModalData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyPlanForm());
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, trainersRes, membersRes, branchesRes] = await Promise.all([
        api.get('/workout-plans'),
        api.get('/staff'),
        api.get('/members', { params: { status: 'active', limit: 2000 } }),
        api.get('/branches'),
      ]);

      setPlans(plansRes?.data || []);
      setTrainers(trainersRes?.data || []);
      setMembers(membersRes?.data || []);
      setBranches(branchesRes?.data || []);
      setModalTrainers(trainersRes?.data || []);
      setModalMembers(membersRes?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load workout plans');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchModalData = useCallback(async (branchId) => {
    setLoadingModalData(true);
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const [trainersRes, membersRes] = await Promise.all([
        api.get('/staff', { params }),
        api.get('/members', { params: { status: 'active', limit: 2000, ...params } }),
      ]);
      setModalTrainers(trainersRes?.data || []);
      setModalMembers(membersRes?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModalData(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(emptyPlanForm());
    setSelectedDay('Monday');
    setModalTrainers(trainers);
    setModalMembers(members);
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    // Convert exercises array to object keyed by day
    const exercisesObj = {};
    if (Array.isArray(plan?.exercises)) {
      plan?.exercises?.forEach(dayPlan => {
        exercisesObj[dayPlan?.day] = dayPlan?.exercises || [];
      });
    }
    const branchId = plan?.branch_id || '';
    setForm({
      title: plan?.title || '',
      branch_id: branchId,
      trainer_id: plan?.trainer_id || '',
      member_id: plan?.member_id || '',
      exercises: exercisesObj,
    });
    setSelectedDay('Monday');
    fetchModalData(branchId);
    setShowModal(true);
  };

  const handleDuplicate = async (plan) => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/workout-plans', {
        trainer_id: plan?.trainer_id || null,
        member_id: null,
        title: `${plan?.title} (Copy)`,
        days: plan?.days || [],
        exercises: plan?.exercises || [],
      });
      setSuccess('Plan duplicated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to duplicate plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    // Block if assigned to active member
    if (plan?.member_id) {
      setError('Cannot delete a plan assigned to an active member. Unassign the member first.');
      setDeleteConfirm(null);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/workout-plans/${plan?.id}`);
      setSuccess('Plan deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!form?.title?.trim()) { setError('Plan title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const activeDays = Object.keys(form?.exercises || {})?.filter(d => (form?.exercises?.[d] || [])?.length > 0);
      const exercisesArray = activeDays?.map(day => ({ day, exercises: form?.exercises?.[day] }));

      if (editingPlan) {
        await api.put(`/workout-plans/${editingPlan?.id}`, {
          title: form?.title?.trim(),
          trainer_id: form?.trainer_id || null,
          member_id: form?.member_id || null,
          days: activeDays,
          exercises: exercisesArray,
        });
      } else {
        await api.post('/workout-plans', {
          trainer_id: form?.trainer_id || null,
          member_id: form?.member_id || null,
          title: form?.title?.trim(),
          days: activeDays,
          exercises: exercisesArray,
        });
      }

      setSuccess(editingPlan ? 'Plan updated successfully!' : 'Plan created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const addExercise = () => {
    setForm(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: [...(prev?.exercises?.[selectedDay] || []), emptyExercise()],
      },
    }));
  };

  const removeExercise = (idx) => {
    setForm(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: (prev?.exercises?.[selectedDay] || [])?.filter((_, i) => i !== idx),
      },
    }));
  };

  const updateExercise = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: (prev?.exercises?.[selectedDay] || [])?.map((ex, i) =>
          i === idx ? { ...ex, [field]: value } : ex
        ),
      },
    }));
  };

  const filteredPlans = plans?.filter(p =>
    p?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (p?.member_name || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (p?.trainer_name || '')?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const currentExercises = form?.exercises?.[selectedDay] || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Workout Plan Builder</h2>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Create and manage workout plans for your members</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Icon name="Plus" size={15} color="#fff" />
          New Plan
        </button>
      </div>
      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertCircle" size={15} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)] flex-1">{error}</p>
          <button onClick={() => setError(null)}><Icon name="X" size={14} color="var(--color-error)" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)]">
          <Icon name="CheckCircle" size={15} color="var(--color-success)" />
          <p className="text-sm text-[var(--color-success)] font-[var(--font-caption)]">{success}</p>
        </div>
      )}
      {/* Search */}
      <div className="relative">
        <Icon name="Search" size={15} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          placeholder="Search plans by name, member, or trainer..."
          className="w-full pl-9 pr-4 py-2.5 bg-[var(--color-input)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
        />
      </div>
      {/* Plan List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-20 bg-[var(--color-muted)] rounded-[var(--radius-sm)] animate-pulse" />
          ))}
        </div>
      ) : filteredPlans?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)]">
          <div className="w-14 h-14 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mb-3">
            <Icon name="Dumbbell" size={26} color="var(--color-primary)" />
          </div>
          <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">
            {search ? 'No plans match your search' : 'No workout plans yet'}
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1 mb-4">
            {search ? 'Try a different search term' : 'Create your first plan to get started'}
          </p>
          {!search && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="Plus" size={14} color="#fff" />
              Create your first plan
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPlans?.map(plan => (
            <div key={plan?.id} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 hover:border-[var(--color-primary)]/30 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] truncate">{plan?.title}</p>
                    {plan?.member_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium font-[var(--font-caption)]">
                        {plan?.member_name}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    {plan?.trainer_name && (
                      <span className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        <Icon name="User" size={11} color="currentColor" />
                        {plan?.trainer_name}
                      </span>
                    )}
                    {plan?.days?.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        <Icon name="Calendar" size={11} color="currentColor" />
                        {plan?.days?.length} day{plan?.days?.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      {new Date(plan?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  {/* Day pills */}
                  {plan?.days?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {plan?.days?.map(d => (
                        <span key={d} className="text-[10px] px-1.5 py-0.5 bg-[var(--color-muted)] text-[var(--color-muted-foreground)] rounded font-[var(--font-caption)]">{d}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEdit(plan)}
                    title="Edit"
                    className="p-1.5 rounded hover:bg-[var(--color-primary)]/10 transition-colors"
                  >
                    <Icon name="Pencil" size={14} color="var(--color-primary)" />
                  </button>
                  <button
                    onClick={() => handleDuplicate(plan)}
                    title="Duplicate"
                    className="p-1.5 rounded hover:bg-[var(--color-accent)]/10 transition-colors"
                  >
                    <Icon name="Copy" size={14} color="var(--color-accent)" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(plan)}
                    title="Delete"
                    className="p-1.5 rounded hover:bg-[var(--color-error)]/10 transition-colors"
                  >
                    <Icon name="Trash2" size={14} color="var(--color-error)" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !saving && setShowModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center">
                  <Icon name="Dumbbell" size={16} color="var(--color-primary)" />
                </div>
                <h3 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
                  {editingPlan ? 'Edit Workout Plan' : 'New Workout Plan'}
                </h3>
              </div>
              <button onClick={() => !saving && setShowModal(false)} className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
                  <Icon name="AlertCircle" size={14} color="var(--color-error)" />
                  <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
                </div>
              )}

              {/* Plan Name */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Plan Name *</label>
                <input
                  type="text"
                  value={form?.title}
                  onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
                  placeholder="e.g. Strength Builder Program"
                  className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                />
              </div>

              {/* Branch */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Branch</label>
                <select
                  value={form?.branch_id}
                  onChange={e => {
                    const branchId = e?.target?.value;
                    setForm(p => ({ ...p, branch_id: branchId, trainer_id: '', member_id: '' }));
                    fetchModalData(branchId);
                  }}
                  className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                >
                  <option value="">All branches</option>
                  {branches?.map(b => <option key={b?.id} value={b?.id}>{b?.name}</option>)}
                </select>
              </div>

              {/* Trainer & Member */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Assign Trainer</label>
                  <select
                    value={form?.trainer_id}
                    onChange={e => setForm(p => ({ ...p, trainer_id: e?.target?.value }))}
                    disabled={loadingModalData}
                    className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors disabled:opacity-60"
                  >
                    <option value="">{loadingModalData ? 'Loading...' : 'Select trainer (optional)'}</option>
                    {modalTrainers?.map(t => <option key={t?.id} value={t?.id}>{t?.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Assign Member</label>
                  <select
                    value={form?.member_id}
                    onChange={e => setForm(p => ({ ...p, member_id: e?.target?.value }))}
                    disabled={loadingModalData}
                    className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors disabled:opacity-60"
                  >
                    <option value="">{loadingModalData ? 'Loading...' : 'Select member (optional)'}</option>
                    {modalMembers?.map(m => <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Day Selector */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-2">Day-by-Day Exercise Builder</label>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {DAYS?.map((day, idx) => {
                    const count = (form?.exercises?.[day] || [])?.length;
                    return (
                      <button
                        key={day}
                        onClick={() => setSelectedDay(day)}
                        className={`relative flex-shrink-0 px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] transition-all ${
                          selectedDay === day
                            ? 'bg-[var(--color-primary)] text-white'
                            : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                        }`}
                      >
                        {DAY_SHORT?.[idx]}
                        {count > 0 && (
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[var(--color-accent)] rounded-full text-[9px] text-white flex items-center justify-center font-bold">
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Exercises for selected day */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-[var(--color-primary)] font-[var(--font-caption)]">{selectedDay} Exercises</p>
                  <button
                    onClick={addExercise}
                    className="flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-[var(--color-primary)]/40 rounded text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/5 transition-colors"
                  >
                    <Icon name="Plus" size={12} color="var(--color-primary)" />
                    Add Exercise
                  </button>
                </div>

                {currentExercises?.length === 0 ? (
                  <div className="py-6 text-center border border-dashed border-[var(--color-border)] rounded-[var(--radius-sm)]">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No exercises for {selectedDay}. Click "Add Exercise" to begin.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {currentExercises?.map((ex, idx) => (
                      <div key={idx} className="p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)]">
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            value={ex?.name}
                            onChange={e => updateExercise(idx, 'name', e?.target?.value)}
                            placeholder="Exercise name"
                            className="flex-1 px-2.5 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)]"
                          />
                          <button onClick={() => removeExercise(idx)} className="p-1 rounded hover:bg-[var(--color-error)]/10 transition-colors flex-shrink-0">
                            <Icon name="Trash2" size={13} color="var(--color-error)" />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { field: 'sets', label: 'Sets' },
                            { field: 'reps', label: 'Reps' },
                            { field: 'rest_time', label: 'Rest' },
                            { field: 'notes', label: 'Notes' },
                          ]?.map(({ field, label }) => (
                            <label key={field} className="flex flex-col gap-0.5">
                              <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{label}</span>
                              <input
                                type={field === 'sets' || field === 'reps' ? 'number' : 'text'}
                                value={ex?.[field]}
                                onChange={e => updateExercise(idx, field, field === 'sets' || field === 'reps' ? Number(e?.target?.value) : e?.target?.value)}
                                placeholder={field === 'rest_time' ? '60s' : field === 'notes' ? 'Optional' : ''}
                                className="px-2 py-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] text-center"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[var(--color-border)]">
              <button
                onClick={() => !saving && setShowModal(false)}
                disabled={saving}
                className="px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form?.title?.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={14} color="#fff" />}
                {saving ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !deleting && setDeleteConfirm(null)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="Trash2" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Delete Plan?</h3>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Are you sure you want to delete <span className="font-semibold text-[var(--color-foreground)]">"{deleteConfirm?.title}"</span>? This action cannot be undone.
                </p>
                {deleteConfirm?.member_id && (
                  <p className="text-xs text-[var(--color-warning)] font-[var(--font-caption)] mt-2 p-2 bg-[var(--color-warning)]/10 rounded">
                    ⚠️ This plan is assigned to an active member and cannot be deleted.
                  </p>
                )}
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleting || !!deleteConfirm?.member_id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {deleting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Trash2" size={14} color="#fff" />}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
